import { Link } from 'react-router-dom';
import { ArrowRight, Browsers, Check, SquaresFour, UserSquare } from '@phosphor-icons/react';
import { PROJECTS, SERVICES } from '../../data/site';
import Container from '../ui/Container';
import IconBadge from '../ui/IconBadge';
import Reveal from '../ui/Reveal';

const ICONS = {
  website: Browsers,
  system: SquaresFour,
  portfolio: UserSquare
};

const TONES = {
  website: {
    panel: 'border border-line bg-surface text-ink shadow-card',
    body: 'text-ink-soft',
    check: 'bg-accent-soft text-accent',
    badge: 'accent'
  },
  system: {
    panel: 'bg-night bg-night-glow text-white shadow-lift',
    body: 'text-white/75',
    check: 'bg-white/15 text-sky',
    badge: 'inverse'
  },
  portfolio: {
    panel: 'bg-brand-gradient text-white shadow-lift',
    body: 'text-white/85',
    check: 'bg-white/20 text-white',
    badge: 'inverse'
  }
};

function ServicePanel({ service, index }) {
  const tone = TONES[service.id];
  const project = PROJECTS.find((item) => item.slug === service.project);
  const Icon = ICONS[service.id];

  return (
    <Reveal from="right" delay={index * 0.06} amount={0.25}>
      <Link
        to="/services"
        className={`group relative block overflow-hidden rounded-4xl transition-transform duration-500 ease-out hover:-translate-y-1 ${tone.panel}`}
      >
        <div className={`grid gap-8 p-7 sm:p-9 ${project ? 'md:grid-cols-[1fr_240px]' : ''}`}>
          <div>
            <IconBadge icon={Icon} tone={tone.badge} />
            <h3 className="mt-6 text-2xl font-bold tracking-tight sm:text-[1.7rem]">{service.title}</h3>
            <p className={`mt-3 max-w-md text-[15px] leading-[1.75] sm:text-base ${tone.body}`}>{service.short}</p>
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {service.includes.slice(0, 4).map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-[14px] font-medium">
                  <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${tone.check}`}>
                    <Check size={11} weight="bold" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {project ? (
            <div className="relative hidden md:block">
              <div className="absolute inset-y-0 right-[-36px] flex w-[300px] items-end">
                <img
                  src={project.thumb}
                  alt={project.alt}
                  width={900}
                  height={562}
                  loading="lazy"
                  className="w-full translate-y-10 rounded-xl border border-white/20 shadow-frame transition-transform duration-700 ease-out group-hover:translate-y-6"
                />
              </div>
            </div>
          ) : (
            <div className="absolute -bottom-10 -right-6 hidden opacity-25 md:block" aria-hidden="true">
              <img src="/main_logo.png" alt="" width={220} height={190} className="w-[220px] rotate-[-12deg]" />
            </div>
          )}
        </div>
      </Link>
    </Reveal>
  );
}

function ServicesShowcase() {
  return (
    <section className="bg-lavender py-20 sm:py-28">
      <Container>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-4">
            <Reveal from="left" className="lg:sticky lg:top-28">
              <h2 className="text-balance text-[1.85rem] font-bold leading-[1.2] tracking-[-0.025em] sm:text-4xl lg:text-[2.75rem]">
                필요한 것만,
                <br />
                제대로 만듭니다
              </h2>
              <p className="mt-5 max-w-sm text-base leading-[1.75] text-ink-soft sm:text-lg">
                홈페이지, 관리 시스템, 포트폴리오. 하나만 맡겨도 되고, 함께 진행할 수도 있습니다.
              </p>
              <Link
                to="/services"
                className="group mt-7 inline-flex items-center gap-1.5 text-[15px] font-semibold text-accent"
              >
                서비스 자세히 보기
                <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="flex flex-col gap-6 lg:col-span-8">
            {SERVICES.map((service, index) => (
              <ServicePanel key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

export default ServicesShowcase;
